import * as React from 'react';

import type {
	W_FieldReport_FindAllReportTypesForCompany_VM_Row as ReportTypeRow,
	W_FieldReport_FindAllReportTypesForCompany_VM_ReportBlockField,
	W_FieldReport_FindAllReportTypesForCompany_VM_ReportTypeBlock,
} from 'ab-api/web/fieldReport/findAllReportTypesForCompany';

import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';

import Radio from 'af-components/Controls/Radio';

import styles from './styles.module.scss';
import { getBlockGroups } from '../Shared/helpers';

export type SelectedFieldRef = {
	reportTypeId: number;
	reportBlockId: number;
	reportBlockFieldId: number;
};

interface Props {
	reportType: ReportTypeRow;
	selectedFields: SelectedFieldRef[];
	onSelectField: (rtId: number, rbId: number, rbfId: number) => void;
	searchTerm: string;
}

export const selectableFieldTypes = [
	ReportBlockFieldEnum.Type.ADDRESS,
	ReportBlockFieldEnum.Type.BOOLEAN,
	ReportBlockFieldEnum.Type.CALCULATED,
	ReportBlockFieldEnum.Type.DROPDOWN,
	ReportBlockFieldEnum.Type.NUMERIC_ATTRIBUTE,
	ReportBlockFieldEnum.Type.EQUIPMENT,
	ReportBlockFieldEnum.Type.INFORMATION,
	ReportBlockFieldEnum.Type.TIME,
	ReportBlockFieldEnum.Type.COMPLETION,
	ReportBlockFieldEnum.Type.TEXTUAL_ATTRIBUTE,
	ReportBlockFieldEnum.Type.ID,
];

const ReportTypeBlocks: React.FC<Props> = (props: Props) => {
	const {
		reportType,
		selectedFields,
		onSelectField,
		searchTerm,
	} = props;

	const isFieldSelected = React.useCallback(
		(rbId: number, rbfId: number) =>
			selectedFields.some(
				(sf) => sf.reportTypeId === reportType.id
					&& sf.reportBlockId === rbId
					&& sf.reportBlockFieldId === rbfId
			),
		[selectedFields, reportType.id]
	);

	const handleCheck = React.useCallback(
		(rbId: number, rbfId: number) => () => {
			onSelectField(reportType.id, rbId, rbfId);
		},
		[onSelectField, reportType.id]
	);

	const renderBlockField = React.useCallback(
		(reportBlockId: number, isPrimary: boolean) =>
			(blockField: W_FieldReport_FindAllReportTypesForCompany_VM_ReportBlockField) => {
				const checked = isFieldSelected(reportBlockId, blockField.id);

				const normalizedSearch = searchTerm.trim().toLowerCase();

				const isMatch =
					normalizedSearch.length > 0 &&
					blockField.name.toLowerCase().includes(normalizedSearch);

				const classes = [
					styles['block-field'],
					styles[`block-field__${isPrimary ? 'primary' : 'secondary'}`],
				];

				if (isMatch) {
					classes.push(styles['block-field__highlighted']);
				}

				return (
					<div className={classes.join(' ')} key={blockField.id}>
						<div className={styles['block-field-radio']}>
							<Radio
								checked={checked}
								label={blockField.name}
								onCheck={handleCheck(reportBlockId, blockField.id)}
							/>
						</div>
					</div>
				);
			},
		[handleCheck, isFieldSelected, searchTerm]
	);

	const renderBlock = React.useCallback((
		block: W_FieldReport_FindAllReportTypesForCompany_VM_ReportTypeBlock,
		isMain: boolean,
		isPrimary: boolean
	) => {
		const reportBlockId = block.reportBlockId;

		const blockClassName = styles[`block__${isPrimary ? 'primary' : 'secondary'}`];

		const blockHeaderClassName = styles[`block-header__${isMain ? 'main' : 'secondary'}`];

		const filteredFields = block.reportBlock.fields.filter((field) => selectableFieldTypes.includes(field.fieldType));

		if (!filteredFields.length) {
			return null;
		}

		return (
			<div className={blockClassName}>
				<div className={blockHeaderClassName}>
					{block.reportBlock.name}
				</div>
				<div className={styles['block-fields']}>
					{filteredFields.map(renderBlockField(reportBlockId, isPrimary))}
				</div>
			</div>
		);
	}, [renderBlockField]);

	const {
		upperTotal,
		primaryMain,
		primaryOther,
		secondaryMain,
		secondaryOther,
		lowerTotal,
	} = React.useMemo(
		() => getBlockGroups(reportType),
		[reportType]
	);

	return (
		<div>

			{upperTotal && <div className={styles['primary-blocks']}>{renderBlock(upperTotal, true, true)}</div>}

			<div className={styles['primary-blocks']}>
				{primaryMain && renderBlock(primaryMain, true, true)}
				{primaryOther.map((rb) => renderBlock(rb, false, true))}
			</div>

			{
				secondaryMain && <div className={styles['secondary-blocks']}>
					{secondaryMain && renderBlock(secondaryMain, true, false)}
					{secondaryOther.map((rb) => renderBlock(rb, false, false))}
				</div>
			}

			{lowerTotal && <div className={styles['primary-blocks']}>{renderBlock(lowerTotal, true, true)}</div>}

		</div>
	);

};

export default React.memo(ReportTypeBlocks);
